import type {
  DiagnosticEntry,
  DiagnosticLevel,
  DiagnosticSource,
  DiagnosticSourceCategory,
} from "./types";

const MAX_ENTRIES = 2500;
const STORAGE_KEY = "nsq-diagnostic-event-log";
const FLUSH_DEBOUNCE_MS = 5000;
const MAX_ENTRY_AGE_MS = 24 * 60 * 60 * 1000; // 24h

interface StoredDiagnosticLog {
  version: 1;
  writeIndex: number;
  count: number;
  entries: (DiagnosticEntry | null)[];
}

/**
 * In-memory ring buffer for timestamped diagnostic events.
 * Singleton — imported and used across SSE clients and axios interceptors.
 *
 * Persists to localStorage with a debounced flush (every 5s after last write)
 * and a synchronous flush on beforeunload. Hydrates from localStorage on
 * construction, pruning entries older than 24h.
 *
 * Memory: bounded to ~2500 entries.
 */
class DiagnosticEventLog {
  private buffer: (DiagnosticEntry | null)[] = [];
  private writeIndex = 0;
  private count = 0;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private dirty = false;
  private storageAvailable: boolean;

  constructor() {
    this.buffer = new Array<DiagnosticEntry | null>(MAX_ENTRIES).fill(null);
    this.storageAvailable = this.probeStorage();
    this.hydrateFromStorage();
    window.addEventListener("beforeunload", () => this.flushSync());
  }

  /**
   * Record a diagnostic event. O(1) — overwrites oldest entry when full.
   */
  record(
    source: DiagnosticSource,
    level: DiagnosticLevel,
    event: string,
    details?: Record<string, unknown>
  ): void {
    const entry: DiagnosticEntry = {
      timestamp: new Date().toISOString(),
      source,
      level,
      event,
      details,
    };

    this.buffer[this.writeIndex] = entry;
    this.writeIndex = (this.writeIndex + 1) % MAX_ENTRIES;
    if (this.count < MAX_ENTRIES) this.count++;

    this.dirty = true;
    this.scheduleDebouncedFlush();
  }

  /**
   * Get all entries, ordered oldest-to-newest.
   * Optionally filter by source category or time window.
   */
  getEntries(filter?: {
    category?: DiagnosticSourceCategory;
    levelFilter?: DiagnosticLevel;
    maxAge?: number;
  }): DiagnosticEntry[] {
    const categorySuffix = filter?.category ? `:${filter.category}` : null;
    const entries: DiagnosticEntry[] = [];
    const startIdx =
      this.count < MAX_ENTRIES ? 0 : this.writeIndex;

    for (let i = 0; i < this.count; i++) {
      const idx = (startIdx + i) % MAX_ENTRIES;
      const entry = this.buffer[idx];
      if (!entry) continue;

      if (categorySuffix && !entry.source.endsWith(categorySuffix)) continue;
      if (filter?.levelFilter && entry.level !== filter.levelFilter) continue;
      if (filter?.maxAge) {
        const age = Date.now() - new Date(entry.timestamp).getTime();
        if (age > filter.maxAge) continue;
      }

      entries.push(entry);
    }

    return entries;
  }

  /**
   * Immediately flush the buffer to localStorage.
   * Called on beforeunload and available for external callers.
   */
  flushSync(): void {
    if (this.flushTimer !== undefined) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    this.flushToStorage();
  }

  private probeStorage(): boolean {
    const testKey = "__nsq_storage_probe__";
    try {
      localStorage.setItem(testKey, "1");
      localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private hydrateFromStorage(): void {
    if (!this.storageAvailable) return;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const stored: StoredDiagnosticLog = JSON.parse(raw);
      if (stored.version !== 1 || !Array.isArray(stored.entries)) return;

      // Walk the stored ring buffer in oldest-to-newest order,
      // pruning entries older than 24h and rebuilding sequentially.
      const storedSize = stored.entries.length;
      const storedCount = Math.min(stored.count, storedSize);
      const startIdx =
        storedCount < storedSize ? 0 : stored.writeIndex % storedSize;
      const now = Date.now();

      for (let i = 0; i < storedCount; i++) {
        const idx = (startIdx + i) % storedSize;
        const entry = stored.entries[idx];
        if (!entry || !entry.timestamp) continue;

        const age = now - new Date(entry.timestamp).getTime();
        if (age > MAX_ENTRY_AGE_MS) continue;

        this.buffer[this.writeIndex] = entry;
        this.writeIndex = (this.writeIndex + 1) % MAX_ENTRIES;
        if (this.count < MAX_ENTRIES) this.count++;
      }
    } catch {
      // Corrupted JSON or unexpected shape — start fresh
    }
  }

  private scheduleDebouncedFlush(): void {
    if (!this.storageAvailable) return;
    if (this.flushTimer !== undefined) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => {
      this.flushTimer = undefined;
      this.flushToStorage();
    }, FLUSH_DEBOUNCE_MS);
  }

  private flushToStorage(): void {
    if (!this.dirty || !this.storageAvailable) return;

    try {
      const payload: StoredDiagnosticLog = {
        version: 1,
        writeIndex: this.writeIndex,
        count: this.count,
        entries: this.buffer,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      this.dirty = false;
    } catch {
      // Quota exceeded — disable persistence for the rest of this session
      this.storageAvailable = false;
    }
  }
}

/** Global singleton */
export const diagnosticEventLog = new DiagnosticEventLog();
